import {
  getHealthRecord,
  MembershipType,
  SortDirection,
} from "@/services/core-api-adapter";
import { MenuItem, Select, Stack, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import HealthRecordEmptyState from "@/features/MyHealth/components/HealthRecordEmptyState";
import HealthRecordList from "@/features/MyHealth/components/HealthRecordList";
import HealthRecordPlaceholder from "@/features/MyHealth/components/HealthRecordPlaceholder";
import LoadingSpinner from "@/features/Common/components/LoadingSpinner";
import useGetMemberships from "@/features/Common/hooks/useGetMemberships";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

export default function HealthRecordContainer() {
  const [isLoading, setIsLoading] = useState(true);
  const [healthRecordData, setHealthRecordData] = useState<any>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string>("All");
  const [sortDirection] = useState<SortDirection>("Desc");

  const [isError, setIsError] = useState<any>(null);
  const { t } = useTranslation();

  const { isMembershipsLoading, firstMembership, membershipsFetchError } =
    useGetMemberships();

  useEffect(() => {
    const memberId = selectedMemberId !== "All" ? selectedMemberId : null;
    const controller = new AbortController();

    setIsLoading(true);
    setIsError(false);
    getHealthRecord(sortDirection, memberId, { signal: controller.signal })
      .then((result) => {
        setHealthRecordData(result);
        setIsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted === false) {
          setIsError(err);
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [selectedMemberId]);

  const hasEvents = healthRecordData?.events?.length > 0 || false;
  const noEvents = healthRecordData?.events?.length === 0 || false;
  const isLoadedWithoutError = !isLoading && !isError;

  function onSelectedMemberChange(el: any) {
    setSelectedMemberId(el.target.value);
  }

  return (
    <>
      {isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING
      ) &&
        !isMembershipsLoading &&
        !membershipsFetchError && (
          <Stack mb={2} spacing={2}>
            <Typography variant="h4">
              {t("HealthRecordList.filter.title")}
            </Typography>
            <Select
              inputProps={{
                "aria-label": t("HealthRecordList.filter.label"),
              }}
              MenuProps={{
                "aria-label": t("HealthRecordList.filter.label"),
              }}
              value={selectedMemberId}
              onChange={onSelectedMemberChange}
            >
              <MenuItem value={"All"}>All</MenuItem>
              {firstMembership.members.map((member) => (
                <MenuItem
                  key={member.memberDetails.id}
                  value={member.memberDetails.id}
                >
                  <Stack>
                    <Typography variant="body1">
                      {member.memberDetails.memberFullName}
                    </Typography>
                    {member.membershipDetails.type ===
                      MembershipType.MAIN_MEMBER && (
                      <Typography variant="body2">
                        {t("HealthRecordList.filter.main.person.label")}
                      </Typography>
                    )}
                  </Stack>
                </MenuItem>
              ))}
              {}
            </Select>
          </Stack>
        )}

      {isLoading ||
        (isMembershipsLoading && (
          <Stack alignItems="center">
            <LoadingSpinner />
          </Stack>
        ))}

      {isLoadedWithoutError && hasEvents && (
        <Stack spacing={2}>
          {!isFeatureEnabled(
            FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING
          ) && (
            <Typography variant="h3">{t("HealthRecordList.title")}</Typography>
          )}
          <HealthRecordList data={healthRecordData} />
        </Stack>
      )}

      {isLoadedWithoutError && noEvents && <HealthRecordEmptyState />}

      {!isLoading && isError && <HealthRecordPlaceholder />}
    </>
  );
}
